import React, { useState, useEffect } from "react";
import { useAtom } from "jotai";
import {
  streamsAtom,
  currentUserAtom,
  agoraClientAtom,
  currentSpaceAtom,
} from "../jotai/atoms";
import { brown, styled } from "../stitches.config";
import Icon from "../components/Icon";
import { Menu, MenuItem, MenuLabel } from "../components/Menu";
import { useAgoraClient, useAgoraControls } from "../av/Agora";
import { Button, SegmentedButtonWrapper } from "../components/Button";
import { useUserInfo } from "../roomservice/maps";
import { useToasts } from "react-toast-notifications";

type IdWithLabel = { deviceId: string; label: string };

function AVControls() {
  // Join agora channel
  const [currentUser] = useAtom(currentUserAtom);
  const [currentSpace] = useAtom(currentSpaceAtom);
  useAgoraClient(currentUser?.id, currentSpace?.id);

  const [userInfo, userInfoClient] = useUserInfo(currentUser?.id);
  const [agoraClient] = useAtom(agoraClientAtom);
  const agoraControls = useAgoraControls(currentUser?.id);
  const [streams] = useAtom(streamsAtom);
  const { addToast } = useToasts();

  const {
    selected,
    onChangeVideo,
    onChangeAudio,
    videoDevices,
    audioDevices,
  } = useAVDevices(agoraControls);

  let hasStream = !!(currentUser && currentUser.id && streams[currentUser.id]);

  useEffect(() => {
    // Set these to off when the stream is first opened in case they were previously true
    if (!hasStream && userInfoClient) {
      userInfoClient?.set("video", false);
      userInfoClient?.set("audio", false);
    }
  }, [hasStream, userInfoClient]);

  if (!currentUser) {
    return null;
  }

  const isMuted = !userInfo?.audio;
  const isStreaming = userInfo?.video;

  const onClickMic = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isMuted) {
      agoraControls.startAudio(() => {
        userInfoClient?.set("audio", true);
      });
    } else {
      agoraControls.stopAudio();
      userInfoClient?.set("audio", false);
    }
  };

  const onClickCam = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isStreaming) {
      navigator.mediaDevices
        .getUserMedia({ audio: true, video: true })
        .then(() => {
          agoraControls.startVideo(() => {
            userInfoClient?.set("video", true);
          });
        })
        .catch((e) => {
          console.error("Get User Media failed", e);

          if (e.name === "NotAllowedError") {
            addToast("Your camera and mic are blocked");
          } else {
            addToast("Permission Error" + e);
          }
        });
    } else {
      agoraControls.stopVideo();
      userInfoClient?.set("video", false);
    }
  };

  return (
    <div style={{ display: "flex" }}>
      <AVButtonWrapper>
        <Button
          disabled={!agoraClient}
          onClick={onClickCam}
          aria-label={isStreaming ? "Turn camera off" : "Turn camera on"}
          title={isStreaming ? "Turn camera off" : "Turn camera on"}
          icon={isStreaming ? "CamOn" : "CamOff"}
          color={!isStreaming ? "destructive" : undefined}
        ></Button>

        <Menu
          disabled={!agoraClient || (!isStreaming && !hasStream)}
          ariaLabel="Select video input"
          icon="Chevron"
          color={!isStreaming ? "destructive" : undefined}
        >
          <MenuLabel>Cameras</MenuLabel>
          {videoDevices.map(({ deviceId, label }) => (
            <MenuItem
              key={deviceId}
              onSelect={(e: Event) => {
                e.preventDefault();
                onChangeVideo(deviceId);
              }}
            >
              <Icon
                icon="Checkmark"
                color={brown}
                style={{
                  opacity: selected.video === deviceId ? 1 : 0,
                }}
              />
              {label}
            </MenuItem>
          ))}
        </Menu>
      </AVButtonWrapper>

      <AVButtonWrapper>
        <Button
          disabled={!agoraClient}
          onClick={onClickMic}
          aria-label={isMuted ? "Unmute" : "Mute"}
          title={isMuted ? "Unmute" : "Mute"}
          icon={isMuted ? "MicOff" : "MicOn"}
          css={isMuted ? { color: "white" } : {}}
          color={isMuted ? "destructive" : undefined}
        ></Button>

        <Menu
          disabled={!agoraClient || (isMuted && !hasStream)}
          ariaLabel="Select audio input"
          icon="Chevron"
          color={isMuted ? "destructive" : undefined}
        >
          <MenuLabel>Microphones</MenuLabel>
          {audioDevices.map(({ deviceId, label }) => (
            <MenuItem
              key={deviceId}
              onSelect={(e: Event) => {
                e.preventDefault();
                onChangeAudio(deviceId);
              }}
            >
              <Icon
                icon="Checkmark"
                color={brown}
                style={{
                  opacity: selected.audio === deviceId ? 1 : 0,
                }}
              />
              {label}
            </MenuItem>
          ))}
        </Menu>
      </AVButtonWrapper>
    </div>
  );
}

function useAVDevices(agoraControls: {
  enumerateAudioDevices: () => Promise<any>;
  enumerateVideoDevices: () => Promise<any>;
  selectAudioInput: (i: string) => void;
  selectVideoInput: (i: string) => void;
}) {
  const [streams] = useAtom(streamsAtom);
  const [currentUser] = useAtom(currentUserAtom);
  const stream = currentUser ? streams[currentUser.id] : undefined;

  const [audioDevices, setAudioDevices] = useState<IdWithLabel[]>([]);
  const [videoDevices, setVideoDevices] = useState<IdWithLabel[]>([]);

  const [currentDeviceIds, setCurrentDeviceIds] = useState<{
    audio?: string;
    video?: string;
  }>({
    audio: undefined,
    video: undefined,
  });

  useEffect(() => {
    if (!stream) {
      return;
    }

    const audioTrack = stream.getAudioTrack();
    const videoTrack = stream.getVideoTrack();

    const audioDevice = audioDevices.find(
      (d) =>
        d?.label === (audioTrack as MediaStreamTrack & { label: string })?.label
    );

    const videoDevice = videoDevices.find(
      (d) =>
        d?.label === (videoTrack as MediaStreamTrack & { label: string })?.label
    );
    setCurrentDeviceIds({
      audio: audioDevice?.deviceId,
      video: videoDevice?.deviceId,
    });
  }, [stream, setCurrentDeviceIds, audioDevices, videoDevices]);

  useEffect(() => {
    agoraControls.enumerateAudioDevices().then((devices) => {
      setAudioDevices(
        devices.map(
          (d: Record<string, any>) =>
            pick(d, ["deviceId", "label"]) as IdWithLabel
        )
      );
    });

    agoraControls.enumerateVideoDevices().then((devices) => {
      setVideoDevices(
        devices.map(
          (d: Record<string, any>) =>
            pick(d, ["deviceId", "label"]) as IdWithLabel
        )
      );
    });
  }, [setAudioDevices, setVideoDevices, agoraControls]);

  const onChangeAudio = (value: string) => {
    setCurrentDeviceIds((currentTracks) => {
      if (currentTracks.audio === value) return currentTracks;
      return {
        ...currentTracks,
        audio: value,
      };
    });
    agoraControls.selectAudioInput(value);
  };

  const onChangeVideo = (value: string) => {
    setCurrentDeviceIds((currentTracks) => {
      if (currentTracks.video === value) return currentTracks;
      return {
        ...currentTracks,
        video: value,
      };
    });
    agoraControls.selectVideoInput(value);
  };

  return {
    selected: currentDeviceIds,
    onChangeVideo,
    onChangeAudio,
    videoDevices,
    audioDevices,
  };
}

const AVButtonWrapper = styled(SegmentedButtonWrapper, {});

export default AVControls;

const pick = (obj: Record<string, any>, props: string[]) => {
  const out: Record<string, any> = {};
  props.forEach((prop) => (out[prop] = obj[prop]));
  return out;
};
